import { defer, EMPTY, EmptyError, Observable, Subject, timer } from 'rxjs';
import { catchError, exhaustMap, filter, first, shareReplay, takeUntil } from 'rxjs/operators';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { PULLING_INTERVAL } from './constants';

export interface IPollResult<T> {
  data$: Observable<T>;
  stopPolling(): void;
}

export function pollUntilDone<T>(
  request: () => Observable<T>,
  isDone: (response: T) => boolean,
  interval: number = PULLING_INTERVAL.FAST,
): IPollResult<T> {
  const stop$ = new Subject<void>();

  const data$ = timer(0, interval).pipe(
    exhaustMap(() =>
      defer(request).pipe(
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.GatewayTimeout) {
            return EMPTY;
          }
          throw error instanceof Error ? error : new Error(String(error));
        }),
      ),
    ),
    takeUntil(stop$),
    filter((response: T | null): response is T => response !== null && isDone(response)),
    first(),
    catchError((error: unknown) => {
      if (error instanceof EmptyError) {
        return EMPTY;
      }
      throw error instanceof Error ? error : new Error(String(error));
    }),
    shareReplay(),
  );

  return {
    data$,
    stopPolling: (): void => {
      stop$.next();
      stop$.complete();
    },
  };
}
